import '@/features/trainings/styles/train.css';
import React, { useContext, useEffect, useState } from 'react';
import { SparkIcon, Divider, Notification, Tabs } from '@spark';
import SideTabsLayout from '@/shared/components/SideTabsLayout';
import { useLocation, useSearchParams } from 'react-router-dom';
import Statistics from '@/features/datasets/components/ExploreDatasets/Statistics';
import { useDataFetching } from '@/features/datasets/hooks/useDataSelectionFetching';
import { ProcessedDataTable } from '@/features/datasets/components/DataSelection/ProcessedDataTable';
import { DatasetTable } from '@/features/datasets/components/DataSelection/DatasetTable';
import DataTableEmptyState from '@/features/datasets/components/DataSelection/DataTableEmptyState';
import { DataEditorTabLabel } from '@/features/datasets/components/DataSelection/DataEditorTabLabel';
import { RawRecordsStatusTable } from '@/features/datasets/components/DataSelection/RawRecordsStatus';
import { GlobalContext } from '@/app/providers/globalState';
import { fetchRawRecordsStats } from '@/features/datasets/components/DataSelection/RawRecordsStatus/service';
import LabelManagementModal from '@/features/tasks/components/LabelManagementModal';
import { ComparisonModeOption, TaskType } from '@/shared/domain/taskTypes';
import { data_from_server_post } from '@/shared/http/client';
import { getFileName } from '@/features/datasets/utils/uploadUtils';
import LoadingTaskData from '@/features/tasks/components/LoadingTaskData';
import { resolveDataSelectionDeepLink } from '@/features/datasets/utils/dataSelectionDeepLink';

const TABLE_PAGE_SIZE = 10;

/**
 * Lightweight Spark-styled accordion section replacing antd Collapse.
 * activeKey / onChange mirror the antd Collapse prop shapes so call-sites
 * need minimal edits.
 */
function SparkExpandSection({ items = [], activeKey = [], onChange }) {
    const toggle = (key) => {
        const next = activeKey.includes(key)
            ? activeKey.filter((k) => k !== key)
            : [...activeKey, key];
        onChange?.(next);
    };
    return (
        <div className="spark-expand-section-root">
            {items.map((item) => {
                const open = activeKey.includes(item.key);
                return (
                    <div key={item.key} className="spark-expand-section-item">
                        <button
                            type="button"
                            className="spark-expand-section-header"
                            aria-expanded={open}
                            onClick={() => toggle(item.key)}
                        >
                            <span className="spark-expand-section-label">{item.label}</span>
                            <SparkIcon
                                slug="chevron-down"
                                size="small"
                                className={`spark-expand-section-chevron${open ? ' spark-expand-section-chevron-open' : ''}`}
                            />
                        </button>
                        <Divider />
                        {open && (
                            <div className="spark-expand-section-body">
                                {item.children}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Tab shell for the datasets view. The task page uses the left side-tab rail
 * (Train/Benchmark + archived tabs); the creation wizard's Data Editor step
 * passes `topTabs` to render the same tabs horizontally instead
 * (Figma 3001-68426) so the table spans nearly the full card width.
 *
 * On the task page the right content pane sits on a white CardSurface; the
 * left rail stays on the page canvas (same pattern as Model Details).
 * When `show` is false (fully empty Data Editor), children render alone —
 * same as Evaluations' empty state.
 */
function DataSelectionTabsRegion({ topTabs, items, activeId, onChange, show = true, children }) {
    if (!show) return children ?? null;
    if (!topTabs) {
        return (
            <SideTabsLayout withCard items={items} activeId={activeId} onChange={onChange}>
                {children}
            </SideTabsLayout>
        );
    }
    return (
        <div style={{ marginTop: 24 }}>
            <Tabs items={items} activeId={activeId} onChange={onChange} size="large" />
            <div style={{ paddingTop: 24 }}>{children}</div>
        </div>
    );
}

const useDataSelection = (currentUsecase, currentFilters = {}, activeSorter = null) => {
    const {
        dataset,
        benchmark,
        datasetMetadata,
        benchmarkMetadata,
        availableExtraParamsKeys,
        crossReferenceFields,
        processedTrainsetInfo,
        isDatasetLoading,
        isBenchmarkLoading,
        handleTableChange,
        setIsInDB,
        isDatasetAndBenchmarkMetadataLoading,
        updateRecord,
    } = useDataFetching(currentUsecase, true, currentFilters, activeSorter);

    const getTrainsetPercent = () => {
        return datasetMetadata.total_num_records === undefined ? 0 : ((datasetMetadata.num_records / datasetMetadata.total_num_records) * 100).toFixed(2);
    };

    const getTestsetPercent = () => {
        return benchmarkMetadata.total_num_records === undefined ? 0 : ((benchmarkMetadata.num_records / benchmarkMetadata.total_num_records) * 100).toFixed(2);
    };

    return {
        dataset,
        benchmark,
        datasetMetadata,
        benchmarkMetadata,
        availableExtraParamsKeys,
        crossReferenceFields,
        processedTrainsetInfo,
        isDatasetLoading,
        isBenchmarkLoading,
        handleTableChange,
        setIsInDB,
        getTrainsetPercent,
        getTestsetPercent,
        isDatasetAndBenchmarkMetadataLoading,
        updateRecord,
    };
};

/**
 * Renders an inline Spark Notification banner when parsing failures are detected.
 * Replaces the previous imperative antd notification.error() call.
 */
const ParsingFailureAlert = ({ setActiveCollapseKeys, statsData, statsLoading, scrollToRawRecordsStatus, notificationDismissed, onDismissNotification }) => {
    const totalFailed = statsData?.failed || 0;
    const hasParsingFailures = totalFailed > 0;

    const handleViewDetailsClick = () => {
        setActiveCollapseKeys(['2']);
        onDismissNotification();
        setTimeout(() => scrollToRawRecordsStatus(), 150);
    };

    if (statsLoading || !hasParsingFailures || notificationDismissed) {
        return null;
    }

    return (
        <div style={{ marginBottom: 16 }}>
            <Notification
                feedback="error"
                title="Parsing Failures Detected"
                content={`${totalFailed} record${totalFailed !== 1 ? 's' : ''} require your attention`}
                actionLabel="View Details"
                onActionClick={handleViewDetailsClick}
                onCloseClick={onDismissNotification}
                wide
            />
        </div>
    );
};

export const DataSelection = ({
    moveToScreen,
    embeddedInTaskDrawer = false,
    onDataOverviewRefresh,
    // Opens the task-page Retrain modal from the "Way to go!" celebration.
    // Only the task page provides it; without it the celebration never shows.
    onRetrainRequest,
    // Creation wizard mode: horizontal top tabs (Train/Benchmark + archived)
    // instead of the left side-tab rail — see DataSelectionTabsRegion.
    topTabs = false,
}) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        getCurrentUsecase,
        refreshUsecases,
        setCurrentUsecaseId
    } = useContext(GlobalContext);

    // Initial usecase from props or context
    const [localUsecase, setLocalUsecase] = React.useState(null);

    // State for raw records stats
    const [statsData, setStatsData] = React.useState(null);
    const [statsLoading, setStatsLoading] = React.useState(false);

    // State to track if the parsing failure notification has been dismissed for this session
    const [parsingFailureNotificationDismissed, setParsingFailureNotificationDismissed] = React.useState(false);
    const [prevFailureCount, setPrevFailureCount] = React.useState(0);

    // State for controlling which collapse panels are open
    const [activeCollapseKeys, setActiveCollapseKeys] = React.useState([]);

    // State for managing the Label Management modal
    const [isLabelModalVisible, setIsLabelModalVisible] = React.useState(false);
    const [labels, setLabels] = React.useState([]);
    const [relationshipTypes, setRelationshipTypes] = React.useState([]);
    const [displayNames, setDisplayNames] = React.useState({});
    // Add counter to track when labels are updated
    const [labelsUpdatedCounter, setLabelsUpdatedCounter] = React.useState(0);
    // Deep-link support: entry points (e.g. the train screen's "Review Unlabeled"
    // action or the FloatingProgressBar "Review them manually" link) can request
    // a tab pre-filtered to the "Unlabeled" ([]) bucket via
    // ?tab=<train-set|benchmark>&labelFilter=unlabeled. Seeded here for mount and
    // re-applied by the effect below when the params appear while mounted.
    const [initialDeepLink] = React.useState(() => resolveDataSelectionDeepLink(searchParams));
    const [trainCurrentFilters, setTrainCurrentFilters] = React.useState(
        initialDeepLink.unlabeledTarget === 'train-set'
            ? initialDeepLink.unlabeledFilters
            : { is_in_dataset: true }
    );
    const [trainActiveSorter, setTrainActiveSorter] = React.useState(null);
    const [testCurrentFilters, setTestCurrentFilters] = React.useState(
        initialDeepLink.unlabeledTarget === 'benchmark'
            ? initialDeepLink.unlabeledFilters
            : { is_in_dataset: true }
    );
    const [testActiveSorter, setTestActiveSorter] = React.useState(null);
    const [currentPage, setCurrentPage] = React.useState(1);
    // Track active tab. Initial value can be seeded from the `tab` URL param so
    // entry points (e.g. the train screen's "Add a Benchmark" link) can deep
    // link straight to a specific tab. The params are consumed (stripped) after
    // every application so the user can freely switch tabs afterwards.
    const [activeTab, setActiveTab] = React.useState(initialDeepLink.activeTab);
    // Separate filters and sorters for archived datasets/benchmarks
    const [archivedDatasetFilters, setArchivedDatasetFilters] = React.useState({});
    const [archivedDatasetSorter, setArchivedDatasetSorter] = React.useState(null);
    const [archivedBenchmarkFilters, setArchivedBenchmarkFilters] = React.useState({});
    const [archivedBenchmarkSorter, setArchivedBenchmarkSorter] = React.useState(null);
    // State for archived counts
    const [archivedDatasetCount, setArchivedDatasetCount] = React.useState(0);
    const [archivedBenchmarkCount, setArchivedBenchmarkCount] = React.useState(0);
    // State for non-archived (active) counts
    const [activeDatasetCount, setActiveDatasetCount] = React.useState(0);
    const [activeBenchmarkCount, setActiveBenchmarkCount] = React.useState(0);
    const [isRecordCountsLoading, setIsRecordCountsLoading] = React.useState(true);

    // Function to scroll to raw records status tab
    const scrollToRawRecordsStatus = () => {
        const element = document.getElementById('raw-records-status-tab');
        if (element) {
            // Scroll to the element with a small offset above it to improve visibility
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Add an additional scroll to ensure better positioning
            setTimeout(() => {
                window.scrollBy({
                    top: 820,  // Adjust this value for more or less scrolling
                    behavior: 'smooth'
                });
            }, 200);
        }
    };

    const handleDismissParsingFailureNotification = () => {
        setParsingFailureNotificationDismissed(true);
    };

    // Get usecase from context only once on mount or when dependencies change
    useEffect(() => {
        const contextUsecase = getCurrentUsecase();
        if (contextUsecase) {
            setLocalUsecase(contextUsecase);
        }
    }, [getCurrentUsecase]);

    // Handle URL parameters on refresh
    useEffect(() => {
        if (!localUsecase) {
            const urlParams = new URLSearchParams(location.search);
            const usecaseId = urlParams.get('usecaseId');

            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
                refreshUsecases();
            }
        }
    }, [location, localUsecase, setCurrentUsecaseId, refreshUsecases]);

    const {
        dataset,
        benchmark,
        datasetMetadata,
        benchmarkMetadata,
        availableExtraParamsKeys,
        crossReferenceFields,
        processedTrainsetInfo,
        isDatasetLoading,
        isBenchmarkLoading,
        handleTableChange,
        setIsInDB,
        getTrainsetPercent,
        getTestsetPercent,
        isDatasetAndBenchmarkMetadataLoading,
        updateRecord,
    } = useDataSelection(localUsecase, trainCurrentFilters, trainActiveSorter);

    // Consume tab/labelFilter deep-link params whenever they appear — not only
    // on mount — so entry points can retarget the Data Editor while it is
    // already open (e.g. clicking "Review them manually" on the floating
    // label-generation warning while on this page). On a fresh mount the state
    // initializers above already consumed the params, the usecase is not
    // loaded yet, and handleTableChange no-ops; here we only strip the URL.
    useEffect(() => {
        if (!searchParams.get('tab') && !searchParams.get('labelFilter')) return;
        const resolved = resolveDataSelectionDeepLink(searchParams);
        if (resolved.hasDeepLink) {
            setActiveTab(resolved.activeTab);
            if (resolved.unlabeledTarget === 'train-set') {
                setTrainCurrentFilters(resolved.unlabeledFilters);
            } else if (resolved.unlabeledTarget === 'benchmark') {
                setTestCurrentFilters(resolved.unlabeledFilters);
            }
            if (resolved.unlabeledTarget) {
                setCurrentPage(1);
                handleTableChange(
                    1,
                    TABLE_PAGE_SIZE,
                    resolved.unlabeledTarget === 'benchmark',
                    resolved.unlabeledFilters,
                    null
                );
            }
        }
        const next = new URLSearchParams(searchParams);
        next.delete('tab');
        next.delete('labelFilter');
        setSearchParams(next, { replace: true });
        // handleTableChange is recreated every render; the params-present guard
        // above keeps this effect inert outside deep-link navigations.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, setSearchParams]);

    // Fetch raw records stats after data is loaded
    useEffect(() => {
        if (localUsecase && !isDatasetAndBenchmarkMetadataLoading) {
            setStatsLoading(true);
            fetchRawRecordsStats({
                clientId: localUsecase.client_id,
                usecaseId: localUsecase._id,
            })
                .then((response) => {
                    setStatsData(response);
                })
                .catch((error) => {
                    console.error("Failed to fetch statistics:", error);
                })
                .finally(() => {
                    setStatsLoading(false);
                });
        }
    }, [localUsecase, isDatasetAndBenchmarkMetadataLoading]);

    // Function to refresh all counts (active and archived) - can be called after archive/unarchive operations
    const refreshRecordCounts = React.useCallback(() => {
        if (localUsecase) {
            data_from_server_post(
                "dataset-records-counts",
                {
                    client_id: localUsecase.client_id,
                    llm_usecase_id: localUsecase._id,
                },
                (result) => {
                    setActiveDatasetCount(result?.active_dataset_count || 0);
                    setActiveBenchmarkCount(result?.active_benchmark_count || 0);
                    setArchivedDatasetCount(result?.archived_dataset_count || 0);
                    setArchivedBenchmarkCount(result?.archived_benchmark_count || 0);
                    setIsRecordCountsLoading(false);
                },
                () => {
                    setIsRecordCountsLoading(false);
                }
            );
        }
    }, [localUsecase]);

    // Fetch all counts on mount and when the usecase changes
    useEffect(() => {
        setIsRecordCountsLoading(true);
        refreshRecordCounts();
    }, [refreshRecordCounts]);

    // Reset notification dismissed state only when the number of failures increases
    useEffect(() => {
        if (!statsLoading && statsData) {
            const currentFailureCount = (statsData.request_example_failed || 0) + (statsData.response_failed || 0);

            if (currentFailureCount > prevFailureCount) {
                setParsingFailureNotificationDismissed(false);
            }

            setPrevFailureCount(currentFailureCount);
        }
    }, [statsData, statsLoading, prevFailureCount]);

    useEffect(() => {
        if (localUsecase && localUsecase?.datasets.length > 0) {
            refreshLabels();
            data_from_server_post(
                'get-relations-label-names',
                localUsecase._id,
                (data) => {
                    if (Array.isArray(data)) {
                        setRelationshipTypes(data);
                    } else {
                        console.error('error in get-relations-label-names:', data);
                    }
                }
            );
            data_from_server_post(
                "get-display-names",
                localUsecase._id,
                (displayNames) => {
                    setDisplayNames(displayNames);
                }
            );
        }
    }, [localUsecase]);

    const onLabelModalClose = () => {
        setIsLabelModalVisible(false);
        // Increment the labels updated counter to trigger refetch in DatasetTable components
        setLabelsUpdatedCounter(prev => prev + 1);

        refreshLabels();
    }

    const refreshLabels = (removedEntity) => {
        if (typeof removedEntity === 'string' && removedEntity) {
            setLabels((prev) => {
                if (!prev || Array.isArray(prev) || typeof prev !== 'object') return prev;
                return {
                    ...prev,
                    entity_names: (prev.entity_names || []).filter((name) => name !== removedEntity),
                };
            });
        }
        data_from_server_post(
            "get-labels",
            localUsecase._id,
            (labels) => {
                setLabels(labels);
            }
        );
    }

    const refreshRelationshipTypes = () => {
        data_from_server_post(
            "get-relations-label-names",
            localUsecase._id,
            (data) => {
                if (Array.isArray(data)) {
                    setRelationshipTypes(data);
                } else {
                    console.error('error in get-relations-label-names:', data);
                }
            }
        );
    }

    const handleCollapseChange = (keys) => {
        setActiveCollapseKeys(keys);
    };

    if (!localUsecase) {
        return <LoadingTaskData />;
    }

    // Show the per-tab empty state only after metadata has finished loading so
    // we don't flash the CTA over data that's still arriving.
    const hasTrainSource = (localUsecase?.datasets?.length || 0) > 0;
    const hasBenchmarkSource = (localUsecase?.benchmarks?.length || 0) > 0;
    const showTrainEmptyState =
        !hasTrainSource && !isDatasetAndBenchmarkMetadataLoading;
    const showBenchmarkEmptyState =
        !hasBenchmarkSource && !isDatasetAndBenchmarkMetadataLoading;
    const showArchivedDatasetEmptyState =
        archivedDatasetCount === 0 && !isDatasetAndBenchmarkMetadataLoading;
    const showArchivedBenchmarkEmptyState =
        archivedBenchmarkCount === 0 && !isDatasetAndBenchmarkMetadataLoading;
    // Fully empty Data Editor: hide the Train/Benchmark side rail and show a
    // single page-level empty card (same chrome as Evaluations).
    const showDataSideTabs =
        hasTrainSource ||
        hasBenchmarkSource ||
        archivedDatasetCount > 0 ||
        archivedBenchmarkCount > 0;

    // Hide the Statistics panel when the active tab has no records —
    // there's nothing to summarize, and the empty card is just visual
    // noise next to the per-tab empty state.
    const currentTabRecordCount = {
        'train-set': activeDatasetCount,
        'benchmark': activeBenchmarkCount,
        'archived-datasets': archivedDatasetCount,
        'archived-benchmarks': archivedBenchmarkCount,
    }[activeTab] ?? 0;
    // Statistics live inside the tab area and reflect the active tab's data.
    // Archived tabs get no Statistics: the metadata endpoint aggregates
    // file-level summaries covering all records, so archived-only stats
    // are not available.
    const currentTabStatisticsMetadata = {
        'train-set': datasetMetadata,
        'benchmark': benchmarkMetadata,
    }[activeTab] ?? null;
    const rawRecordsStatusItems = [];
    if (statsData?.total > 0) {
        rawRecordsStatusItems.push({
            key: '2',
            label: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    Raw Records Status
                    {(statsData?.failed > 0) && (
                        <span
                            className="spark-expand-section-fail-badge"
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveCollapseKeys(['2']);
                                setTimeout(() => scrollToRawRecordsStatus(), 150);
                            }}
                        >
                            {statsData.failed}
                        </span>
                    )}
                </div>
            ),
            children: <RawRecordsStatusTable
                currentUsecase={localUsecase}
                isLoading={isDatasetAndBenchmarkMetadataLoading}
                statsData={statsData}
                setStatsData={setStatsData}
            />,
        },);
    }
    // Embedded in the task page: horizontal inset comes from task-page.css
    // (`.task-page-tab-body > .data-selection-page--embedded`).
    const pageShellStyle = embeddedInTaskDrawer ? undefined : { marginTop: '20px' };

    return (
        <div
            className={embeddedInTaskDrawer ? 'data-selection-page--embedded' : 'page-content-shell'}
            style={pageShellStyle}
        >
            <div hoverable="true">
                {/* TODO: remove the localUsecase?.task_type !== TaskType.NER when we'll enable NER training*/}
                <ProcessedDataTable
                    processedTrainsetInfo={processedTrainsetInfo}
                    loading={isDatasetAndBenchmarkMetadataLoading}
                />

                <DataSelectionTabsRegion
                    topTabs={topTabs}
                    show={showDataSideTabs}
                    items={[
                        { id: 'train-set', label: <DataEditorTabLabel label="Train Set" count={activeDatasetCount} isLoading={isRecordCountsLoading} /> },
                        { id: 'benchmark', label: <DataEditorTabLabel label="Benchmark" count={activeBenchmarkCount} isLoading={isRecordCountsLoading} /> },
                        { id: 'archived-datasets', label: <DataEditorTabLabel label="Archived Datasets" count={archivedDatasetCount} isLoading={isRecordCountsLoading} /> },
                        { id: 'archived-benchmarks', label: <DataEditorTabLabel label="Archived Benchmarks" count={archivedBenchmarkCount} isLoading={isRecordCountsLoading} /> },
                    ]}
                    activeId={activeTab}
                    onChange={setActiveTab}
                >
                {!showDataSideTabs ? (
                    !isDatasetAndBenchmarkMetadataLoading && (
                        <DataTableEmptyState
                            variant="train"
                            usecase={localUsecase}
                            uploadTarget="dataset"
                            layout="page"
                        />
                    )
                ) : (
                <>
                {activeTab === 'train-set' && (
                    showTrainEmptyState ? (
                        <DataTableEmptyState
                            variant="train"
                            usecase={localUsecase}
                            uploadTarget="dataset"
                        />
                    ) : (
                        <DatasetTable
                            key="train-set-table"
                            title={null}
                            dataset={dataset}
                            metadata={datasetMetadata}
                            availableExtraParamsKeys={availableExtraParamsKeys?.dataset || []}
                            crossReferenceFields={crossReferenceFields}
                            isLoading={isDatasetLoading}
                            currentUsecase={localUsecase}
                            isBenchmark={false}
                            handleTableChange={handleTableChange}
                            setIsInDB={setIsInDB}
                            pageSize={TABLE_PAGE_SIZE}
                            labelsUpdated={labelsUpdatedCounter}
                            onOpenLabelModal={() => setIsLabelModalVisible(true)}
                            currentFilters={trainCurrentFilters}
                            activeSorter={trainActiveSorter}
                            setCurrentFilters={setTrainCurrentFilters}
                            setActiveSorter={setTrainActiveSorter}
                            datasetName={localUsecase?.datasets?.[0]}
                            allLabelOptions={labels}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            refreshCategories={refreshLabels}
                            relationshipTypes={relationshipTypes}
                            refreshRelationshipTypes={refreshRelationshipTypes}
                            updateRecord={updateRecord}
                            isArchived={false}
                            refreshRecordCounts={refreshRecordCounts}
                            onDataOverviewRefresh={onDataOverviewRefresh}
                            onRetrainRequest={onRetrainRequest}
                        />
                    )
                )}

                {activeTab === 'benchmark' && (
                    showBenchmarkEmptyState ? (
                        <DataTableEmptyState
                            variant="benchmark"
                            usecase={localUsecase}
                            uploadTarget="benchmark"
                        />
                    ) : (
                        <DatasetTable
                            key="benchmark-table"
                            title={null}
                            dataset={benchmark}
                            metadata={benchmarkMetadata}
                            availableExtraParamsKeys={availableExtraParamsKeys?.benchmark || []}
                            crossReferenceFields={crossReferenceFields}
                            isLoading={isBenchmarkLoading}
                            currentUsecase={localUsecase}
                            isBenchmark={true}
                            handleTableChange={handleTableChange}
                            setIsInDB={setIsInDB}
                            pageSize={TABLE_PAGE_SIZE}
                            labelsUpdated={labelsUpdatedCounter}
                            onOpenLabelModal={() => setIsLabelModalVisible(true)}
                            currentFilters={testCurrentFilters}
                            activeSorter={testActiveSorter}
                            setCurrentFilters={setTestCurrentFilters}
                            setActiveSorter={setTestActiveSorter}
                            datasetName={localUsecase?.benchmarks?.[0]}
                            allLabelOptions={labels}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            relationshipTypes={relationshipTypes}
                            updateRecord={updateRecord}
                            isArchived={false}
                            refreshRecordCounts={refreshRecordCounts}
                            onDataOverviewRefresh={onDataOverviewRefresh}
                        />
                    )
                )}

                {activeTab === 'archived-datasets' && (
                    showArchivedDatasetEmptyState ? (
                        <DataTableEmptyState variant="archived-datasets" />
                    ) : (
                    <DatasetTable
                        key="archived-datasets-table"
                        title={null}
                        dataset={dataset}
                        metadata={datasetMetadata}
                        availableExtraParamsKeys={availableExtraParamsKeys?.dataset || []}
                        crossReferenceFields={crossReferenceFields}
                        isLoading={isDatasetLoading}
                        currentUsecase={localUsecase}
                        isBenchmark={false}
                        handleTableChange={handleTableChange}
                        setIsInDB={setIsInDB}
                        pageSize={TABLE_PAGE_SIZE}
                        labelsUpdated={labelsUpdatedCounter}
                        onOpenLabelModal={() => setIsLabelModalVisible(true)}
                        currentFilters={archivedDatasetFilters}
                        activeSorter={archivedDatasetSorter}
                        setCurrentFilters={setArchivedDatasetFilters}
                        setActiveSorter={setArchivedDatasetSorter}
                        datasetName={localUsecase?.datasets?.[0]}
                        allLabelOptions={labels}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        refreshCategories={refreshLabels}
                        relationshipTypes={relationshipTypes}
                        refreshRelationshipTypes={refreshRelationshipTypes}
                        updateRecord={updateRecord}
                        isArchived={true}
                        refreshRecordCounts={refreshRecordCounts}
                        onDataOverviewRefresh={onDataOverviewRefresh}
                    />
                    )
                )}

                {activeTab === 'archived-benchmarks' && (
                    showArchivedBenchmarkEmptyState ? (
                        <DataTableEmptyState variant="archived-benchmarks" />
                    ) : (
                    <DatasetTable
                        key="archived-benchmarks-table"
                        title={null}
                        dataset={benchmark}
                        metadata={benchmarkMetadata}
                        availableExtraParamsKeys={availableExtraParamsKeys?.benchmark || []}
                        crossReferenceFields={crossReferenceFields}
                        isLoading={isBenchmarkLoading}
                        currentUsecase={localUsecase}
                        isBenchmark={true}
                        handleTableChange={handleTableChange}
                        setIsInDB={setIsInDB}
                        pageSize={TABLE_PAGE_SIZE}
                        labelsUpdated={labelsUpdatedCounter}
                        onOpenLabelModal={() => setIsLabelModalVisible(true)}
                        currentFilters={archivedBenchmarkFilters}
                        activeSorter={archivedBenchmarkSorter}
                        setCurrentFilters={setArchivedBenchmarkFilters}
                        setActiveSorter={setArchivedBenchmarkSorter}
                        datasetName={localUsecase?.benchmarks?.[0]}
                        allLabelOptions={labels}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        relationshipTypes={relationshipTypes}
                        updateRecord={updateRecord}
                        isArchived={true}
                        refreshRecordCounts={refreshRecordCounts}
                        onDataOverviewRefresh={onDataOverviewRefresh}
                    />
                    )
                )}

                {currentTabRecordCount > 0 && currentTabStatisticsMetadata && (
                    <SparkExpandSection
                        activeKey={activeCollapseKeys}
                        onChange={handleCollapseChange}
                        items={[{
                            key: '1',
                            label: <span className="fs-unmask">Statistics</span>,
                            children: <Statistics datasetMetaData={currentTabStatisticsMetadata} />,
                        }]}
                    />
                )}
                </>
                )}
                </DataSelectionTabsRegion>

                {/* parsing failure alert component */}
                <ParsingFailureAlert
                    currentUsecase={localUsecase}
                    isLoading={isDatasetAndBenchmarkMetadataLoading}
                    setActiveCollapseKeys={setActiveCollapseKeys}
                    statsData={statsData}
                    statsLoading={statsLoading}
                    scrollToRawRecordsStatus={scrollToRawRecordsStatus}
                    notificationDismissed={parsingFailureNotificationDismissed}
                    onDismissNotification={handleDismissParsingFailureNotification}
                />

                {rawRecordsStatusItems.length > 0 && (
                    <div id="raw-records-status-tab">
                        <SparkExpandSection
                            activeKey={activeCollapseKeys}
                            onChange={handleCollapseChange}
                            items={rawRecordsStatusItems}
                        />
                    </div>
                )}

                <LabelManagementModal
                    visible={isLabelModalVisible}
                    clientId={localUsecase.client_id}
                    llmUsecaseId={localUsecase._id}
                    onClose={onLabelModalClose}
                    labelsObject={labels}
                    displayNames={displayNames}
                    setDisplayNames={setDisplayNames}
                    taskType={localUsecase?.task_type}
                />
            </div>
        </div>
    );
};